import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  useRevealTutorPayoutAccount,
  useSetTutorCommission,
  useTutorCommissions,
  useTutorPayoutAccount,
} from '../../api/tutor-applications';
import { ApplicationStatusBadge, Badge } from '../../components/Badge';
import { Modal } from '../../components/Modal';
import { Spinner } from '../../components/Spinner';
import { ApiError } from '../../lib/api';
import { formatDate, formatDateTime } from '../../lib/format';
import type { TutorProfile } from '../../types/api';

/**
 * The tutor half of a user's detail page, from spec 32: where the
 * application stands, where their money goes, and what the platform takes.
 *
 * The payout account is masked here and revealed only on a deliberate press
 * — that read writes an audit row, so it cannot happen because a page
 * rendered.
 */
export function TutorApplicationPanel({
  tutorId,
  profile,
}: {
  tutorId: string;
  profile: TutorProfile;
}) {
  const commissions = useTutorCommissions(tutorId);
  const payout = useTutorPayoutAccount(tutorId);
  const reveal = useRevealTutorPayoutAccount();
  const setCommission = useSetTutorCommission();

  const [open, setOpen] = useState(false);
  const [percent, setPercent] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const current = commissions.data?.[0];
  const account = payout.data?.account ?? null;

  // The same rule the queue's badge uses: approved, still teaching, and not
  // able to take anything new until all three are on file.
  const notReadyToBePaid =
    profile.applicationStatus === 'approved' &&
    (!current || !account);

  async function save() {
    setError(null);
    try {
      await setCommission.mutateAsync({
        tutorId,
        commissionPercent: Number(percent),
        note: note.trim(),
      });
      setOpen(false);
      setPercent('');
      setNote('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not work.');
    }
  }

  return (
    <div className="panel p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-ink">Application</h2>
        <ApplicationStatusBadge status={profile.applicationStatus} />
      </div>

      {notReadyToBePaid && (
        <p className="mb-3 rounded-lg border border-rule bg-surface-2 p-2 text-xs text-ink-2">
          <Badge tone="danger">Missing documents</Badge>{' '}
          Approved, so their booked lessons go ahead — but they cannot be given
          anything new until their payout account and commission are complete.
        </p>
      )}

      <dl className="space-y-3">
        <div>
          <dt className="text-xs text-ink-3">Commission</dt>
          <dd className="mt-0.5 flex items-baseline gap-2 text-sm text-ink">
            {commissions.isLoading ? (
              '—'
            ) : current ? (
              <>
                <span className="tabular-nums">{current.commissionPercent}%</span>
                <span className="text-xs text-ink-3">
                  since {formatDate(current.effectiveFrom)}
                </span>
              </>
            ) : (
              <span className="text-ink-3">Not set</span>
            )}
            <button
              type="button"
              className="link ms-auto text-xs"
              onClick={() => {
                setPercent(current ? String(current.commissionPercent) : '20');
                setOpen(true);
              }}
            >
              Change
            </button>
          </dd>
        </div>

        {/* Append-only, so "what did we take from her in March" has an
            answer. Oldest entries stay visible rather than being replaced. */}
        {commissions.data && commissions.data.length > 1 && (
          <div>
            <dt className="text-xs text-ink-3">History</dt>
            <dd className="mt-1 space-y-1">
              {commissions.data.slice(1).map((rate) => (
                <p key={rate.id} className="text-xs text-ink-2">
                  <span className="tabular-nums">{rate.commissionPercent}%</span> from{' '}
                  {formatDate(rate.effectiveFrom)}
                  {rate.setByName && <span className="text-ink-3"> · {rate.setByName}</span>}
                  {rate.note && <span className="block text-ink-3">{rate.note}</span>}
                </p>
              ))}
            </dd>
          </div>
        )}

        <div>
          <dt className="text-xs text-ink-3">Payout account</dt>
          <dd className="mt-0.5 text-sm text-ink">
            {payout.isLoading ? (
              '—'
            ) : account ? (
              <>
                <span className="block">
                  {account.method.nameEn}
                  {account.bankName && ` · ${account.bankName}`}
                </span>
                <span className="mt-0.5 block font-mono text-xs text-ink-2" dir="ltr">
                  {reveal.data?.accountRef ?? account.accountRefMasked}
                </span>
                {account.holderName && (
                  <span className="mt-0.5 block text-xs text-ink-3">
                    {account.holderName}
                  </span>
                )}
                {!reveal.data && (
                  <button
                    type="button"
                    className="link mt-1 text-xs"
                    disabled={reveal.isPending}
                    onClick={() => reveal.mutate({ tutorId })}
                  >
                    Reveal (logged)
                  </button>
                )}
              </>
            ) : (
              <span className="text-ink-3">None</span>
            )}
          </dd>
        </div>

        {profile.applicationStatus !== 'approved' && (
          <div>
            <Link to="/tutor-applications" className="link text-xs">
              Open the application queue
            </Link>
          </div>
        )}
      </dl>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Change the commission"
        description="A new rate, not an edit: past lessons keep the rate they were taught at."
        footer={
          <>
            <button type="button" className="btn" onClick={() => setOpen(false)}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={setCommission.isPending || percent === '' || !note.trim()}
              onClick={() => void save()}
            >
              {setCommission.isPending && <Spinner />}
              Save
            </button>
          </>
        }
      >
        <label className="label" htmlFor="newCommission">
          Commission %
        </label>
        <input
          id="newCommission"
          type="number"
          min={0}
          max={100}
          step={0.01}
          className="input"
          value={percent}
          onChange={(e) => setPercent(e.target.value)}
        />
        <label className="label mt-3" htmlFor="commissionNote">
          Why
        </label>
        <textarea
          id="commissionNote"
          rows={2}
          className="input"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        {current && (
          <p className="mt-2 text-xs text-ink-3">
            The current rate was set {formatDateTime(current.createdAt)}.
          </p>
        )}
        {error && (
          <p role="alert" className="mt-2 text-sm text-danger">
            {error}
          </p>
        )}
      </Modal>
    </div>
  );
}
